import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, FormControl, FormGroup, ControlLabel } from 'react-bootstrap';
import Utils from '../../util/Utils.jsx';
import { analyzeSchedule, analyzeRepository, generateScheduleNarrative } from '../../util/ai/analyze.js';
import { NARRATIVE_TASK } from '../../util/ai/prompts.js';
import { priorDatasetOptions, priorSelectionHint, suggestPriorDataset } from '../../util/ai/suggestPriorDataset.js';
import { formatNarrativeProgress, localNarrativeStatus, narrativeDebugText } from '../../util/ai/narrativeProgress.js';
import { extractTaskCodes, matchTaskCodes } from '../../util/ai/serializeTask.js';

const AUDIENCES = ['Foreman', 'Project Manager', 'Executive Stakeholder'];

const AiInsightsTab = ({ projects, dispatch }) => {
	const [health, setHealth] = useState(null);
	const [projectId, setProjectId] = useState('');
	const [context, setContext] = useState(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const [mode, setMode] = useState('schedule');
	const [analysis, setAnalysis] = useState('');
	const [narrativeScope, setNarrativeScope] = useState(NARRATIVE_TASK);
	const [audience, setAudience] = useState('Project Manager');
	const [useTemplate, setUseTemplate] = useState(false);
	const [priorId, setPriorId] = useState('');
	const [emailOn, setEmailOn] = useState(false);
	const [debugOn, setDebugOn] = useState(false);
	const [debugInfo, setDebugInfo] = useState(null);
	const [narrativeNote, setNarrativeNote] = useState('');
	const [codeWarning, setCodeWarning] = useState('');
	const [narrativeProgress, setNarrativeProgress] = useState('');
	const [provider, setProvider] = useState('local');

	const projectDatasets = useMemo(() => {
		const map = {};
		(projects || []).forEach(project => {
			map[project.id] = { Id: project.id, Name: project.name };
		});
		return map;
	}, [projects]);
	const currentProject = projectId ? projectDatasets[projectId] : null;
	const suggestedPrior = useMemo(
		() => suggestPriorDataset(currentProject, projectDatasets),
		[currentProject, projectDatasets]
	);
	const priorOptions = useMemo(
		() => priorDatasetOptions(currentProject, projectDatasets),
		[currentProject, projectDatasets]
	);
	const priorHint = priorSelectionHint(priorId, suggestedPrior);
	useEffect(() => {
		setPriorId(suggestedPrior || '');
	}, [projectId, suggestedPrior]);

	const loadHealth = useCallback(async () => {
		try {
			const result = await Utils.ajaxAsync('adminAiHealth', {}, dispatch, { suppressErrorAlert: true });
			setHealth(result);
			if (result && (result.preferredProvider === 'gemini' || result.preferredProvider === 'local')) {
				setProvider(result.preferredProvider);
			}
		} catch (e) {
			setHealth({ success: false, configured: false, reachable: false, error: e.message });
		}
	}, [dispatch]);

	useEffect(() => {
		loadHealth();
	}, [loadHealth]);

	const suggested = useMemo(() => {
		if (!analysis || !context) return { matched: [], unmatched: [] };
		return matchTaskCodes(extractTaskCodes(analysis), context.tasks);
	}, [analysis, context]);

	const loadContext = async () => {
		if (!projectId) return;
		setBusy(true);
		setError('');
		setAnalysis('');
		setContext(null);
		try {
			const result = await Utils.ajaxAsync('adminAiProjectContext', { projectId, provider }, dispatch, { suppressErrorAlert: true });
			if (!result.success) {
				setError(result.error || 'Unable to load project context');
			} else {
				setContext(result);
			}
		} catch (e) {
			setError(e.message || 'Unable to load project context');
		} finally {
			setBusy(false);
		}
	};

	const runAnalysis = async (dryRun) => {
		if (mode !== 'narrative' && !context) return;
		if (mode === 'narrative' && !projectId) return;
		setBusy(true);
		setError('');
		setAnalysis('');
		setDebugInfo(null);
		setNarrativeNote('');
		setCodeWarning('');
		setNarrativeProgress('');
		try {
			if (mode === 'schedule') {
				setAnalysis(await analyzeSchedule(context.tasks, dispatch));
			} else if (mode === 'repository') {
				setAnalysis(await analyzeRepository(context.tasks, context.notes || [], dispatch));
			} else {
				const result = await generateScheduleNarrative({
					dataset: projectId,
					priorDataset: priorId,
					provider,
					scope: narrativeScope,
					audience,
					fieldVoice: useTemplate ? 1 : '',
					email: !dryRun && emailOn ? 1 : '',
					debug: debugOn || dryRun ? 1 : '',
					dryRun: dryRun ? 1 : ''
				}, dispatch, (status) => setNarrativeProgress(formatNarrativeProgress(status)));
				setDebugInfo(result && result.debug ? result.debug : null);
				if (result && result.dryRun) {
					setNarrativeNote('Dry run. The model was not called.');
				} else if (!result || result.success === false || result.status === 'error') {
					const msg = (result && (result.error || result.hint)) || 'Narrative request failed';
					setError(msg);
					setNarrativeNote(msg);
				} else if (!result.text || !String(result.text).trim()) {
					const msg = (result && result.error) || 'The model returned no narrative text.';
					setError(msg);
					setNarrativeNote(msg);
				} else {
					setAnalysis(result.text);
					setCodeWarning((result && (result.codeWarning || (result.debug && result.debug.codeWarning))) || '');
					if (result.emailSent) setNarrativeNote('Emailed the narrative to you.');
					else if (emailOn && result.emailError) setNarrativeNote(result.emailError);
				}
			}
		} catch (e) {
			setError(e.message || 'AI request failed');
		} finally {
			setBusy(false);
			setNarrativeProgress('');
		}
	};

	const setAiProvider = async (next) => {
		setProvider(next);
		setBusy(true);
		setError('');
		try {
			const result = await Utils.ajaxAsync('adminAiSetProvider', { provider: next }, dispatch, { suppressErrorAlert: true });
			if (!result.success) {
				setError(result.error || 'Unable to save AI provider');
			} else {
				setHealth(result);
				if (projectId) {
					const ctx = await Utils.ajaxAsync('adminAiProjectContext', { projectId, provider: next }, dispatch, { suppressErrorAlert: true });
					if (ctx.success) {
						setContext(ctx);
					}
				}
			}
		} catch (e) {
			setError(e.message || 'Unable to save AI provider');
		} finally {
			setBusy(false);
		}
	};

	const localReady = health && health.localConfigured && health.reachable;
	const geminiReady = health && health.geminiConfigured;
	const selectedReady = provider === 'gemini' ? geminiReady : localReady;
	const localStatus = localNarrativeStatus(health, 'admin');
	const geminiStatus = !health
		? 'Checking…'
		: !health.cloudAllowed
			? 'Disabled (CLOUD_ALLOWED)'
			: health.geminiConfigured
				? `Configured (${health.geminiModel || 'Gemini'})`
				: 'Not configured — add GEMINI_API_KEY to .env';

	return (
		<section className="superAdminSection">
			<div className="superAdminSectionHead">
				<h2>AI Insights (beta)</h2>
				<Button bsSize="small" disabled={busy} onClick={loadHealth}>Recheck connection</Button>
			</div>
			<p className="superAdminAiStatus" data-state={selectedReady ? 'ok' : 'warn'}>
				Active: {provider === 'gemini' ? 'Gemini Cloud' : 'Local LLM'}
			</p>
			<FormGroup>
				<ControlLabel>AI model</ControlLabel>
				<div className="superAdminAiProviderSwitch">
					<label className="radio-inline">
						<input
							type="radio"
							name="aiProvider"
							checked={provider === 'local'}
							disabled={busy}
							onChange={() => setAiProvider('local')}
						/>
						{' '}Local LLM
					</label>
					<label className="radio-inline">
						<input
							type="radio"
							name="aiProvider"
							checked={provider === 'gemini'}
							disabled={busy}
							onChange={() => setAiProvider('gemini')}
						/>
						{' '}Gemini Cloud
					</label>
				</div>
				<p className="superAdminAiMeta">Local: {localStatus}</p>
				<p className="superAdminAiMeta">Gemini: {geminiStatus}</p>
			</FormGroup>
			<p>
				Read-only beta stolen from SWA_ai schedule / notes / narrative prompts.
				Does not write flags or change project data. Connection settings live in
				server <code>.env</code> only (not the browser bundle). The switch is saved
				for Super Admin AI Insights.
			</p>

			<FormGroup>
				<ControlLabel>Project</ControlLabel>
				<FormControl
					componentClass="select"
					value={projectId}
					onChange={e => setProjectId(e.target.value)}>
					<option value="">Select a project…</option>
					{projects.map(p => (
						<option key={p.id} value={p.id}>
							#{p.id} — {p.name}{p.ownerEmail ? ` (${p.ownerEmail})` : ''}
						</option>
					))}
				</FormControl>
			</FormGroup>
			<Button bsStyle="primary" disabled={busy || !projectId} onClick={loadContext}>
				Load schedule context
			</Button>

			{context && (
				<p className="superAdminAiMeta">
					Loaded {context.tasks.length} of {context.totalTasks} activities
					{context.truncated ? ` (lowest-float cap ${context.cap})` : ''}.
					Field notes: {(context.notes || []).length}.
				</p>
			)}

			<FormGroup>
				<ControlLabel>Capability</ControlLabel>
				<FormControl
					componentClass="select"
					value={mode}
					onChange={e => setMode(e.target.value)}>
					<option value="schedule">Analyzer — schedule risks</option>
					<option value="repository">Updates — map field notes</option>
					<option value="narrative">Narrative</option>
				</FormControl>
			</FormGroup>

			{mode === 'narrative' && (
				<div>
					<FormGroup>
						<ControlLabel>Prior weekly update</ControlLabel>
						<FormControl
							componentClass="select"
							value={priorId}
							onChange={e => setPriorId(e.target.value)}>
							<option value="">No prior update</option>
							{priorOptions.map(option => (
								<option key={option.id} value={option.id}>
									{option.sameProject ? '' : 'Different schedule — '}#{option.id} — {option.name}
								</option>
							))}
						</FormControl>
						<p className="superAdminAiMeta">{priorHint.text}</p>
					</FormGroup>
					<FormGroup>
						<ControlLabel>Request</ControlLabel>
						<FormControl
							componentClass="textarea"
							rows={6}
							value={narrativeScope}
							onChange={e => setNarrativeScope(e.target.value)}
						/>
					</FormGroup>
					<FormGroup>
						<ControlLabel>Audience</ControlLabel>
						<FormControl
							componentClass="select"
							value={audience}
							onChange={e => setAudience(e.target.value)}>
							{AUDIENCES.map(name => (
								<option key={name} value={name}>{name}</option>
							))}
						</FormControl>
					</FormGroup>
					<FormGroup>
						<label className="checkbox-inline">
							<input
								type="checkbox"
								checked={useTemplate}
								onChange={e => setUseTemplate(e.target.checked)}
							/>
							{' '}Use a plain field-update voice
						</label>
					</FormGroup>
					<FormGroup>
						<label className="checkbox-inline">
							<input type="checkbox" checked={emailOn} onChange={e => setEmailOn(e.target.checked)} />
							{' '}Email me the narrative when it finishes
						</label>
					</FormGroup>
					<FormGroup>
						<label className="checkbox-inline">
							<input type="checkbox" checked={debugOn} onChange={e => setDebugOn(e.target.checked)} />
							{' '}Debug — show the digest, prompt, model, tokens, and timing
						</label>
					</FormGroup>
				</div>
			)}

			<Button
				bsStyle="primary"
				className="superAdminActionBtn"
				disabled={busy || !selectedReady || (mode === 'narrative' ? !projectId : !context)}
				onClick={() => runAnalysis(false)}>
				{busy ? 'Working…' : 'Run'}
			</Button>
			{mode === 'narrative'
				? (
					<Button bsSize="small" disabled={busy || !projectId} onClick={() => runAnalysis(true)}>
						Dry run
					</Button>
				)
				: null}

			{narrativeProgress ? <p className="superAdminAiMeta">{narrativeProgress}</p> : null}
			{narrativeNote ? <p className="superAdminAiMeta">{narrativeNote}</p> : null}
			{error && <p className="superAdminDangerBanner">{error}</p>}
			{debugInfo
				? (
					<pre className="superAdminAiOutput">{narrativeDebugText(debugInfo)}</pre>
				)
				: null}

			{analysis && (
				<div className="superAdminAiOutput">
					<h3>Model output</h3>
					<pre>{analysis}</pre>
					{codeWarning ? <p>{codeWarning}</p> : null}
					{suggested.matched.length > 0 && (
						<p>
							Bracketed activity IDs (preview only, not flagged):{' '}
							{suggested.matched.join(', ')}
						</p>
					)}
				</div>
			)}
		</section>
	);
};

AiInsightsTab.propTypes = {
	projects: PropTypes.array.isRequired,
	dispatch: PropTypes.func.isRequired
};

export default AiInsightsTab;
